import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import dayjs from 'dayjs';
import { Label } from '@components/Label';
import { SearchTextField } from '@components/inputs';
import { TableBodyCell, TableHeaderCell } from '@components/table';
import { fonts } from '@theme/fontsCustomer';
import { ErrorIcon } from '@icons/ErrorIcon';
import type { TechnicalInquiryListFilter } from '@repositories/technicalSupportRepository/technicalSupport.types';
import { useSearchParamsFilter } from '@customHooks/useSearchParamsFilter';
import { useGetInfiniteTechnicalInquiryList } from '@queryHooks/useTechnicalSupport';
import { getSortOrder } from '@utils/CommonUtil';

type TableHeaderProps = {
  keyword?: string;
  totalElement?: number;
  handleChangeKeyword: (keyword: string) => void;
};

type TableHeadRowProps = {
  queries: TechnicalInquiryListFilter;
  handleChangeSort: (orderBy: string, order: SortOrder | undefined) => void;
};

const TableHeader = ({ keyword, totalElement, handleChangeKeyword }: TableHeaderProps) => {
  const { t } = useTranslation();
  return (
    <TableHeaderWrap>
      <TableHeaderTitle>
        {t('Technical_Support_List.Total')}
        <span className="counter">{totalElement ?? 0}</span>
      </TableHeaderTitle>
      <StyledTextField
        placeholder={t('Technical_Support_List.Product_Name_Search')}
        value={keyword}
        onChange={handleChangeKeyword}
        onSearch={handleChangeKeyword}
      />
    </TableHeaderWrap>
  );
};

const TableHeadRow = ({ queries, handleChangeSort }: TableHeadRowProps) => {
  const { t } = useTranslation();
  return (
    <TableHead>
      <TableRow>
        <StyledTableHeaderCell align="center" width="72px">
          {t('Technical_Support_List.Number')}
        </StyledTableHeaderCell>
        <StyledTableHeaderCell
          width="140px"
          orderBy="categoryName"
          order={getSortOrder(queries.sort, 'categoryName')}
          onSortChange={handleChangeSort}
        >
          {t('Technical_Support_List.Product_Category')}
        </StyledTableHeaderCell>
        <StyledTableHeaderCell
          width="260px"
          orderBy="productName"
          order={getSortOrder(queries.sort, 'productName')}
          onSortChange={handleChangeSort}
        >
          {t('Technical_Support_List.Product_Name')}
        </StyledTableHeaderCell>
        <StyledTableHeaderCell
          width="428px"
          orderBy="planRate"
          order={getSortOrder(queries.sort, 'planRate')}
          onSortChange={handleChangeSort}
        >
          {t('Technical_Support_List.Plan_Pricing')}
        </StyledTableHeaderCell>
        <StyledTableHeaderCell
          width="160px"
          orderBy="userName"
          order={getSortOrder(queries.sort, 'userName')}
          onSortChange={handleChangeSort}
        >
          {t('Technical_Support_List.Member_Name')}
        </StyledTableHeaderCell>
        <StyledTableHeaderCell
          width="240px"
          orderBy="account"
          order={getSortOrder(queries.sort, 'account')}
          onSortChange={handleChangeSort}
        >
          {t('Technical_Support_List.Purchase_Request_Account')}
        </StyledTableHeaderCell>
        <StyledTableHeaderCell
          width="180px"
          orderBy="createdDate"
          order={getSortOrder(queries.sort, 'createdDate')}
          onSortChange={handleChangeSort}
        >
          {t('Technical_Support_List.Inquiry_Date')}
        </StyledTableHeaderCell>
        <StyledTableHeaderCell
          width="120px"
          orderBy="status"
          order={getSortOrder(queries.sort, 'status')}
          onSortChange={handleChangeSort}
        >
          {t('Technical_Support_List.Processing_Status')}
        </StyledTableHeaderCell>
      </TableRow>
    </TableHead>
  );
};

export const TechnicalSupportListTable = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigator = useNavigate();
  const { tenantId } = useParams();
  const [queries, setQueries] = useSearchParamsFilter<TechnicalInquiryListFilter>({
    defaultFilter: {
      sort: [],
      keyword: '',
    },
    whiteList: ['sort', 'keyword'],
  });
  const { data: technicalInquiry } = useGetInfiniteTechnicalInquiryList({ ...queries, tenantId });
  const technicalInquiryFlat = technicalInquiry?.pages.flatMap(data => data.content);

  const handleChangeKeyword = (value: string) => {
    setQueries(prev => ({ ...prev, keyword: value }));
  };

  const handleChangeSort = (orderBy: string, order: SortOrder | undefined) => {
    setQueries(prevFilter => ({
      ...prevFilter,
      sort: order ? [`${orderBy}.${order}`] : [],
    }));
  };

  return (
    <TableLayout>
      <TableHeader
        keyword={queries.keyword}
        totalElement={technicalInquiry?.pages[0].totalElements}
        handleChangeKeyword={handleChangeKeyword}
      />
      <TableWrap>
        <Table>
          <TableHeadRow queries={queries} handleChangeSort={handleChangeSort} />
          <TableBody>
            {technicalInquiryFlat?.length ? (
              technicalInquiryFlat.map((data, index) => {
                return (
                  <TableRow key={data.technicalInquiryId} onClick={() => navigator(`${data.technicalInquiryId}`)}>
                    <TableBodyCell align="center">{index}</TableBodyCell>
                    <TableBodyCell>{data.categoryName}</TableBodyCell>
                    <TableBodyCell>
                      <ProductLabel>
                        <ProductLabelIcon src={data.productIconUrl} />
                        {data.productName}
                      </ProductLabel>
                    </TableBodyCell>
                    <TableBodyCell>{data.planName}</TableBodyCell>
                    <TableBodyCell>{data.userName}</TableBodyCell>
                    <TableBodyCell>{data.account}</TableBodyCell>
                    <TableBodyCell>{dayjs(data.createdDate).format('YYYY.MM.DD')}</TableBodyCell>
                    <TableBodyCell>
                      <Label variant="contain" color={data.conversionStatusColor}>
                        {data.conversionStatus}
                      </Label>
                    </TableBodyCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableBodyCell colSpan={8}>
                  <Empty>
                    <div className="icon">
                      <ErrorIcon color={theme.colors['ic-primary-light']} />
                    </div>
                    <span>{t('Technical_Support_List.No_History')}</span>
                  </Empty>
                </TableBodyCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrap>
    </TableLayout>
  );
};

const TableLayout = styled.section`
  margin-top: 40px;
`;

const TableHeaderWrap = styled.article`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledTextField = styled(SearchTextField)`
  width: 250px;
`;

const TableHeaderTitle = styled.div`
  ${fonts.Headline8}
  display: flex;
  gap: 4px;
  padding: 8px 0 4px 16px;
  .counter {
    color: ${({ theme }) => theme.colors['text-primary-light']};
  }
`;

const TableWrap = styled(TableContainer)`
  width: 100%;
  margin-top: 16px;
`;

const StyledTableHeaderCell = styled(TableHeaderCell)`
  padding: 11px 12px;
`;

const ProductLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ProductLabelIcon = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 4px;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  height: 126px;

  & .icon {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 100%;
    background: ${props => props.theme.colors['bg-primary-lighter']};
  }

  ${fonts.Body2};
  color: ${props => props.theme.colors['text-gray-sub-dark']};
`;
